// Unified /search page — queries /api/search across Blog + Academy + Shop.
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search as SearchIcon, FileText, Film, Frame, ArrowRight,
} from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import { useT, useLang } from "@/lib/i18n";
import { useCurrency } from "@/context/CurrencyContext";
import { apiFetch } from "@/lib/api";

const KINDS = ["all", "blog", "academy", "shop"];

const SearchPage = () => {
  const t = useT();
  const lang = useLang();
  const { formatFromEUR } = useCurrency();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [activeKind, setActiveKind] = useState("all");
  const [data, setData] = useState({ items: [], groups: { blog: 0, academy: 0, shop: 0 }, total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => { setInput(q); }, [q]);

  useEffect(() => {
    let cancelled = false;
    if (!q.trim()) { setData({ items: [], groups: { blog: 0, academy: 0, shop: 0 }, total: 0 }); return; }
    setLoading(true);
    (async () => {
      const { ok, data: d } = await apiFetch(`/api/search?q=${encodeURIComponent(q.trim())}&limit=30&language=${lang}`);
      if (!cancelled) {
        if (ok) {
          setData({ items: d?.items || [], groups: d?.groups || {}, total: d?.total || 0 });
        } else {
          setData({ items: [], groups: { blog: 0, academy: 0, shop: 0 }, total: 0 });
        }
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, lang]);

  const onSubmit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (input.trim()) next.set("q", input.trim()); else next.delete("q");
    setParams(next);
  };

  const filtered = useMemo(() => {
    if (activeKind === "all") return data.items;
    return data.items.filter((it) => it.kind === activeKind);
  }, [data.items, activeKind]);

  return (
    <Layout testId="page-search">
      <SEO title={t("searchP.seo.title")} description={t("searchP.seo.desc")} />

      <section className="bg-[color:var(--gv-mist)] pt-12 pb-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("blogP.bc.home") }, { label: t("searchP.title") }]} />
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mt-6 mb-3" data-testid="search-title">{t("searchP.title")}</h1>
          <p className="text-[15px] text-[color:var(--gv-ink)]/70 max-w-2xl">{t("searchP.intro")}</p>

          <form onSubmit={onSubmit} className="mt-6 relative max-w-2xl">
            <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
            <input
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("searchP.placeholder")}
              className="w-full h-12 pl-11 pr-4 bg-white border border-[color:var(--gv-line)] rounded-[5px] text-[15px] focus:border-[color:var(--gv-pink)]/40 outline-none"
              data-testid="search-input"
            />
          </form>

          {q && (
            <div className="mt-5 flex items-center gap-2 overflow-x-auto" data-testid="search-kind-chips">
              {KINDS.map((kind) => {
                const count = kind === "all" ? data.total : (data.groups[kind] || 0);
                return (
                  <button
                    key={kind}
                    onClick={() => setActiveKind(kind)}
                    className={`shrink-0 px-3 py-1.5 rounded-[5px] text-[12.5px] transition-colors ${activeKind === kind ? "bg-[color:var(--gv-ink)] text-white" : "bg-white text-[color:var(--gv-ink)] border border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"}`}
                    data-testid={`search-chip-${kind}`}
                  >
                    {t(`searchP.chip.${kind}`)} <span className="opacity-50 ml-1">({count})</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {!q && (
            <div className="text-center py-16">
              <SearchIcon size={32} className="mx-auto text-[color:var(--gv-ink)]/30 mb-4" />
              <div className="text-[15px] text-[color:var(--gv-ink)]/55">{t("searchP.empty.prompt")}</div>
            </div>
          )}
          {q && loading && (
            <div className="text-center py-12 text-[color:var(--gv-ink)]/55">{t("searchP.loading")}</div>
          )}
          {q && !loading && filtered.length === 0 && (
            <div className="text-center py-16">
              <SearchIcon size={32} className="mx-auto text-[color:var(--gv-ink)]/30 mb-4" />
              <div className="font-display text-xl text-[color:var(--gv-ink)] mb-2" data-testid="search-empty-title">{t("searchP.empty.no_results_title")}</div>
              <div className="text-[13.5px] text-[color:var(--gv-ink)]/55">{t("searchP.empty.no_results_desc")}</div>
            </div>
          )}
          {q && !loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="search-results">
              {filtered.map((it) => <ResultCard key={`${it.kind}-${it.slug}`} item={it} t={t} formatFromEUR={formatFromEUR} />)}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

const KIND_META = {
  blog: { Icon: FileText, badgeKey: "searchP.kind.blog" },
  academy: { Icon: Film, badgeKey: "searchP.kind.academy" },
  shop: { Icon: Frame, badgeKey: "searchP.kind.shop" },
};

const ResultCard = ({ item, t, formatFromEUR }) => {
  const meta = KIND_META[item.kind] || KIND_META.blog;
  const { Icon } = meta;
  const price = item.from_amount_cents ? formatFromEUR(item.from_amount_cents) : null;
  return (
    <Link
      to={item.url}
      className="group block bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)] transition-colors"
      data-testid={`search-result-${item.kind}-${item.slug}`}
    >
      <div className="aspect-video bg-[color:var(--gv-mist)] relative overflow-hidden">
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[color:var(--gv-ink)]/20"><Icon size={28} /></div>
        )}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-[5px] bg-white/95 text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)]">
          <Icon size={11} /> {t(meta.badgeKey)}
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-display text-[16px] text-[color:var(--gv-ink)] leading-snug mb-2 group-hover:text-[color:var(--gv-pink)] transition-colors line-clamp-2">{item.title}</h3>
        {item.excerpt && <p className="text-[13px] text-[color:var(--gv-ink)]/70 leading-relaxed line-clamp-2 mb-3">{item.excerpt}</p>}
        <div className="flex items-center justify-between text-[12.5px] text-[color:var(--gv-ink)]/55">
          <span className="inline-flex items-center gap-1">
            {price ? <span className="text-[color:var(--gv-pink)] font-medium">{t("searchP.from")} {price}</span> : <span>{t(meta.badgeKey)}</span>}
          </span>
          <ArrowRight size={13} className="text-[color:var(--gv-pink)] group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

export default SearchPage;
